import { useMemo } from "react";
import { useGetAllSitesQuery } from "../redux/services/siteApiSlice";
import { useGetAllZonesQuery } from "../redux/services/zoneApiSlice";
import { removeEmptyKeys } from "../utils";

type SiteRow = { id?: string; name?: string };
type ZoneRow = { id?: string; name?: string; siteId?: string };

/** Sites and zones for the current tenant (for report filter dropdowns). */
export function useTenantSitesAndZones(tenantId: string | undefined) {
  const sitesQuery = useGetAllSitesQuery(removeEmptyKeys({ tenantId }), { skip: !tenantId });
  const zonesQuery = useGetAllZonesQuery(removeEmptyKeys({ tenantId }), { skip: !tenantId });

  const siteRows = useMemo(() => (sitesQuery.data?.result?.rows ?? []) as SiteRow[], [sitesQuery.data]);
  const zoneRows = useMemo(() => (zonesQuery.data?.result?.rows ?? []) as ZoneRow[], [zonesQuery.data]);

  const siteNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of siteRows) {
      if (s?.id != null) m.set(String(s.id), String(s.name ?? ""));
    }
    return m;
  }, [siteRows]);

  const zoneNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const z of zoneRows) {
      if (z?.id != null) m.set(String(z.id), String(z.name ?? ""));
    }
    return m;
  }, [zoneRows]);

  const siteOptionsById = useMemo(
    () =>
      siteRows.map((s) => ({
        label: String(s.name ?? s.id ?? ""),
        value: String(s.id),
      })),
    [siteRows]
  );

  const zoneOptionsById = useMemo(
    () =>
      zoneRows.map((z) => ({
        label:
          z.siteId && siteNameById.get(String(z.siteId))
            ? `${z.name ?? ""} (${siteNameById.get(String(z.siteId))})`
            : String(z.name ?? z.id ?? ""),
        value: String(z.id),
      })),
    [zoneRows, siteNameById]
  );

  return {
    sitesQuery,
    zonesQuery,
    siteRows,
    zoneRows,
    siteNameById,
    zoneNameById,
    siteOptionsById,
    zoneOptionsById,
  };
}
